// ─────────────────────────────────────────────────────────────────────────────
// Colours of the presence wall (#814): a programme is a HUE, each of its groups a STEP of
// that hue. Lives in utils/ rather than in the component because UI-CHARTE.md §4 puts every
// domain colour mapping here — a table copied into a .vue is how the five presence-status
// tables diverged (#742).
//
// These are territory fills painted UNDER the cells, not status colours: a cell's own
// background stays the opaque status colour, which `presence-status-display.ts` owns. The
// two never mix.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Programme hues, as `r,g,b` triplets usable straight inside an `rgba()`. Deliberately far
 * apart on the wheel: two programmes side by side have to read as different at a glance, and
 * a wall can show six or more at once.
 */
const PROGRAM_HUES = [
  '14,165,233',   // sky
  '234,88,12',    // orange
  '22,163,74',    // green
  '168,85,247',   // purple
  '220,38,38',    // red
  '13,148,136',   // teal
  '202,138,4',    // amber
  '79,70,229',    // indigo
] as const;

/** Fill opacity of a territory. Raised from .34, which left the hues too washed out to read. */
export const TERRITORY_FILL_ALPHA = 0.58;

/** How much the outline is darkened from its own fill. */
export const TERRITORY_STROKE_DARKEN = 0.34;

/**
 * Steps of one hue, for the groups of a programme — the proportion mixed in, SIGNED:
 * positive goes towards white, negative towards black.
 *
 * The sign is the whole point. With every step going towards white, a programme's three
 * groups all landed in the same family of pales and stopped being tellable apart; signed,
 * the programme reads as five clearly distinct levels — plain, pale, dark, very pale, deep.
 * The order is not monotonic on purpose: two neighbouring groups must contrast, and
 * neighbours are consecutive in this table.
 *
 * The proof of concept documented this correction in its README but never carried it in its
 * code, where the table was still `[0, .34, .16, .52, .25, .43]` — unsigned, capped at .52.
 * See `.docs/poc-mur-presence/DECISIONS.md`.
 */
const GROUP_STEPS = [0, 0.38, -0.30, 0.76, -0.55, 0.55] as const;

/** The hue of a programme, by its rank in the legend. */
export function programHue(rank: number): string {
  return PROGRAM_HUES[((rank % PROGRAM_HUES.length) + PROGRAM_HUES.length) % PROGRAM_HUES.length];
}

/**
 * One step of a hue: towards white for a positive mix, towards black for a negative one.
 * Takes and returns an `r,g,b` triplet.
 */
export function shade(rgb: string, step: number): string {
  const mix = GROUP_STEPS[((step % GROUP_STEPS.length) + GROUP_STEPS.length) % GROUP_STEPS.length];
  const target = mix >= 0 ? 255 : 0;
  const f = Math.abs(mix);
  return rgb
    .split(',')
    .map(Number)
    .map(v => Math.round(v + (target - v) * f))
    .join(',');
}

/** A darkened version of a hue, for the stroke that outlines its territory. */
export function darken(rgb: string, factor = TERRITORY_STROKE_DARKEN): string {
  return rgb
    .split(',')
    .map(Number)
    .map(v => Math.round(v * (1 - factor)))
    .join(',');
}

/**
 * An OPAQUE colour: `hex` laid over white at `alpha`. A cell background must be opaque,
 * otherwise the territory painted underneath bleeds through and muddies the status.
 */
export function overWhite(hex: string, alpha: number): string {
  const n = Number.parseInt(hex.replace('#', ''), 16);
  const channels = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  return `rgb(${channels.map(v => Math.round(255 + (v - 255) * alpha)).join(',')})`;
}
